import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getSectionWithCategories, getVideoUrl, urlFor } from "../../services/sanity";
import type { Category, Section } from "../../types";

interface SectionViewProps {
  sectionId?: string; // For scroll-based sections on homepage
  sectionSlug?: string; // For standalone section page
  index?: number; // For alternating backgrounds (even = dark, odd = light)
}

export default function SectionView({
  sectionId,
  sectionSlug,
  index = 0,
}: SectionViewProps) {
  // Alternating background colors: even = light, odd = dark
  const isLight = index % 2 === 0;
  const backgroundColor = isLight ? "#f5f5f7" : "#101216";
  const textColor = isLight ? "#1d1d1f" : "#ffffff";
  const textColorMuted = isLight ? "#6e6e73" : "#d2d2d7"; // apple-gray-600 / apple-gray-300
  const [section, setSection] = useState<
    (Section & { categories?: Category[]; directProjectCount?: number }) | null
  >(null);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    async function fetchSection() {
      if (!sectionSlug) return;

      try {
        const data = await getSectionWithCategories(sectionSlug);
        setSection(data);
      } catch (error) {
        console.error("Error fetching section:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchSection();
  }, [sectionSlug]);

  // Intersection Observer for auto-play/pause video
  useEffect(() => {
    if (!section?.backgroundVideo || !videoRef.current || !sectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = videoRef.current;
          if (!video) return;

          if (entry.isIntersecting) {
            video.play().catch((err) => console.log("Auto-play prevented:", err));
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, [section?.backgroundVideo]);

  if (loading) {
    return (
      <section
        id={sectionId}
        className="min-h-screen flex items-center justify-center"
        style={{ height: "100dvh", backgroundColor }}
      >
        <div className="text-2xl" style={{ color: textColor }}>Loading...</div>
      </section>
    );
  }

  if (!section) {
    return null;
  }

  const hasCategories = section.categories && section.categories.length > 0;
  const hasDirectProjects = (section.directProjectCount ?? 0) > 0;
  const hasContent = hasCategories || hasDirectProjects;

  return (
    <section
      ref={sectionRef}
      id={sectionId}
      className="relative min-h-dvh flex items-center justify-center px-6 py-20 snap-start snap-always overflow-hidden"
      style={{ backgroundColor }}
    >
      {/* Background Thumbnail - fallback when no video */}
      {!section.backgroundVideo && section.thumbnail && (
        <div className="absolute inset-0 opacity-20">
          <img
            src={urlFor(section.thumbnail).width(1920).height(1080).url()}
            alt={section.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto w-full">
        <div className="text-center mb-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-headline mb-4"
            style={{
              color: textColor,
              textShadow: isLight
                ? "0 0 30px rgba(255, 162, 0, 0.2), 0 0 60px rgba(255, 162, 0, 0.1)"
                : "0 0 30px rgba(255, 162, 0, 0.4), 0 0 60px rgba(255, 162, 0, 0.2)",
            }}
          >
            {section.name}
          </motion.h2>
          {section.description && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="text-body max-w-2xl mx-auto"
              style={{ color: textColorMuted }}
            >
              {section.description}
            </motion.p>
          )}
        </div>

        {/* Call-to-Action Button */}
        {hasContent ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="text-center mb-6"
          >
            <Link
              to={`/section/${section.slug.current}`}
              className="inline-block px-12 py-4 text-white rounded-full font-bold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-transform gradient-anim"
            >
              Projekte entdecken</Link>
          </motion.div>
        ) : (
          // No categories and no direct projects - Show "Coming Soon" message
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="text-center mb-6"
          >
            <p className="text-lg" style={{ color: textColorMuted }}>
              Coming soon - Projekte in Arbeit..
            </p>
          </motion.div>
        )}

      </div>

      {/* Video - full width at bottom, semi-transparent */}
      {section.backgroundVideo && (
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.15 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="absolute inset-0 z-0"
        >
          <div
            className="w-full h-full"
            style={{
              maskImage: 'linear-gradient(to bottom, transparent 0%, black 50%, black 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 50%, black 100%)',
            }}
          >
            <video
              ref={videoRef}
              src={getVideoUrl(section.backgroundVideo.asset._ref)}
              poster={section.thumbnail ? urlFor(section.thumbnail).width(1920).height(1080).url() : undefined}
              className="w-full h-full object-cover object-center"
              loop
              muted
              playsInline
            />
          </div>
        </motion.div>
      )}
    </section>
  );
}
